import { isBoolean } from 'lodash';
import { parse } from '../../parse';
import moment from 'moment';
import { serverTimestamp } from 'firebase/firestore';
import { collection, doc, setDoc } from 'firebase/firestore';

const generateUniqueCode = ({ length = 12 }) => {
  const characters = '023456789ABCDEFGHJKLMNOPQRSTUVWXYZ';
  let code = '';
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    code += characters[randomIndex];
  }
  return code;
};

const logDocumentEvent = async ({
  db,
  databaseId,
  contentTypeName,
  documentAction,
  documentId,
  path,
  before,
  after,
  source
}) => {
  const newDocRef = doc(collection(db, 'document_events'));

  const data = {
    id: newDocRef.id,
    contentTypeName,
    documentAction,
    documentId,
    databaseId: databaseId || '(default)',
    path,
    deleted: false,
    before: before || {},
    after: after || {},
    lastUpdatedBy: 'logDocumentEvent',
    source,
    //expiration: Timestamp.fromDate(new Date(new Date().getTime() + 3600000)),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(doc(db, 'document_events', newDocRef.id), data, {
    merge: true
  });
};

/* determines the value of a step
        - a step may have placeholder values referring to currently selected documents e.g. ${ds.id.whatever}
    */
const getStepFieldValue = ({
  stepField,
  underlyingField,
  selections,
  documents,
  document,
  user,
  firebaseUser,
  local,
  data,
  params,
  history,
  dataLinks
}) => {
  if (!stepField?.value === undefined) {
    return;
  }

  let value = stepField.value;

  if (value === null || value === undefined) {
    return '';
  }

  if (value.indexOf('{unique_code') > -1) {
    let length = 12;
    const match = value.match(/\{unique_code_(\d+)\}/);
    if (match && match.length > 1) {
      length = parseInt(match[1], 10);
      return generateUniqueCode({ length });
    } else {
      return generateUniqueCode({ length });
    }
  } else if (['date', 'datetime'].some(p => p === value)) {
    value = serverTimestamp();
    return value;
  } else if (
    value?.toLowerCase &&
    (value?.toLowerCase() === 'day' || value?.toLowerCase() === 'daynumber')
  ) {
    return moment().day();
  } else if (value?.toLowerCase && value?.toLowerCase() === 'dayname') {
    return moment().format('dddd');
  } else if (value?.toLowerCase && value?.toLowerCase() === 'week') {
    return moment().week();
  } else if (
    value?.toLowerCase &&
    (value?.toLowerCase() === 'month' || value?.toLowerCase() === 'monthnumber')
  ) {
    return moment().month();
  } else if (value?.toLowerCase && value?.toLowerCase() === 'monthname') {
    return moment().format('MMMM');
  } else if (value?.toLowerCase && value?.toLowerCase() === 'year') {
    return moment().year();
  }

  if (isBoolean(value)) {
    return value;
  } else if (value === 'true') {
    return true;
  } else if (value === 'true') {
    return false;
  }

  return parse({
    markup: value,
    underlyingField,
    actionSequenceExecutionHistory: history,
    documents,
    selections,
    user,
    firebaseUser,
    local,
    document,
    data,
    params,
    dataLinks
  });
};

/* get the current document from the data link set against the supplied step */
const getStepDocument = ({ selections, documents, dataLinkId }) => {
  let document;
  if (selections && selections[dataLinkId]) {
    const dataLinkDocumentList = documents[dataLinkId];

    if (dataLinkDocumentList) {
      document = dataLinkDocumentList.documents.find(
        d => d.id === selections[dataLinkId].documentId
      );
    }
  }
  return document;
};

const validateForm = ({ formId }) => {
  const form = window.document.getElementById(formId);

  if (!form) {
    return false;
  } else if (form?.checkValidity() === false) {
    // This is the magic function that displays the validation errors to the user
    form.reportValidity();
    return false;
  }

  return true;
};

const getForms = () => {
  let formsData = {};

  const forms = document.querySelectorAll('form');

  // Iterate over each form
  forms.forEach(form => {
    if (!form.id) {
      return;
    }

    formsData[form.id] = {};

    Array.from(form.elements).forEach(element => {
      const data = {
        id: element.id,
        value: element.value,
        name: element.name,
        ...element.dataset
      };

      if (element.type === 'checkbox') {
        if (!Array.isArray(formsData[form.id]['checkboxes'])) {
          formsData[form.id]['checkboxes'] = [];
        }
        formsData[form.id]['checkboxes'].push({
          ...data,
          checked: element.checked
        });
      } else if (element.type === 'radio') {
        if (!Array.isArray(formsData[form.id]['radios'])) {
          formsData[form.id]['radios'] = [];
        }
        formsData[form.id]['radios'].push({
          ...data,
          checked: element.checked
        });
      }

      if (element.id) {
        if (formsData[form.id][element.id] !== undefined) {
          // we have already added an element with same id, possibly checkboxes with same id, so ignore
          delete formsData[form.id][element.id];
        } else {
          formsData[form.id][element.id] = data;
        }
      }
    });
  });

  return formsData;
};

const getFormValues = ({
  formId,
  inputTypes = ['input', 'textarea', 'select'],
  returnObject = false
}) => {
  const form = window?.document?.getElementById(formId);
  if (!form) {
    return { error: 'invalid form id' };
  }
  /*else if (form?.checkValidity() === false) {
      // This is the magic function that displays the default browser validation errors to the user
      form.reportValidity();
      return false;
    }*/

  let inputs = [...form?.elements]
    .map(element => {
      let val = element.value;
      if (element.getAttribute('type')?.toLowerCase() === 'checkbox') {
        val = element.checked;
      }
      return {
        id: element.id,
        value: val,
        type: element.nodeName
      };
    })
    .filter(e => inputTypes.some(type => type === e.type.toLowerCase()));

  if (returnObject) {
    let data = {};
    inputs.forEach(input => {
      data[input.id] = input.value;
    });
    return data;
  }

  return inputs;
};

export {
  getStepFieldValue,
  getStepDocument,
  validateForm,
  getFormValues,
  getForms,
  logDocumentEvent
};
